/**
 * NOLAS 2026, (c) Mikaël FOURNY.
 * src/pwa/activationOuverture.ts
 *
 * Rôle :
 *   Décider, quand une nouvelle version de l'application est prête, s'il faut
 *   l'ACTIVER TOUT DE SUITE (un seul rechargement) ou seulement AFFICHER le
 *   bandeau non bloquant (updateBanner.ts).
 *
 * Pourquoi (Mika, 2026-10-04) : « l'ancien design reste présent sur mon
 * iPhone même après avoir actualisé ». En mode `prompt`, la nouvelle version
 * s'installe et ATTEND ; une actualisation ne l'active pas (l'onglet reste
 * contrôlé par l'ancienne), et sur iPhone une application posée sur l'écran
 * d'accueil n'est presque jamais fermée. Le bandeau seul ne suffit donc pas.
 *
 * La règle :
 *   - à l'OUVERTURE (premières secondes, ou retour au premier plan après une
 *     longue absence), tant que personne n'a rien touché ni rien saisi, on
 *     active la nouvelle version et on recharge UNE fois : rien n'est perdu ;
 *   - dès qu'une session est engagée (un geste, une saisie, un champ rempli),
 *     on n'impose plus rien : bandeau, comme avant ;
 *   - JAMAIS de rechargement automatique tant que la file hors ligne des scans
 *     n'est pas vide ou qu'elle est en cours d'envoi (src/offline/) ;
 *   - garde anti-boucle en sessionStorage : au plus un rechargement
 *     automatique par fenêtre de cinq minutes dans un même onglet.
 *
 * Particularités de Safari iOS et de l'application installée : pas de
 * fermeture, donc le retour au premier plan (visibilitychange) après au moins
 * cinq minutes d'absence rouvre une « phase d'ouverture », sauf si une saisie
 * a eu lieu dans cette page (un formulaire peut être à moitié rempli).
 *
 * Mikael - chef de projet - 2026-10-04
 */

/** Durée de la phase d'ouverture, depuis le chargement ou le retour au premier plan. */
export const DUREE_OUVERTURE_MS = 15 * 1000;
/** Absence minimale pour qu'un retour au premier plan vaille une nouvelle ouverture. */
export const ABSENCE_REOUVERTURE_MS = 5 * 60 * 1000;
/** Garde anti-boucle : pas deux rechargements automatiques dans ce délai. */
export const DELAI_ANTI_BOUCLE_MS = 5 * 60 * 1000;
/**
 * Clé sessionStorage de la garde anti-boucle. Nom neutre, commun à toutes les
 * instances de la flotte. Mikael - chef de projet - 2026-10-04
 */
export const CLE_GARDE = 'app.maj.rechargementAuto';

/** Champs dont la saisie engage la session. */
const SELECTEUR_CHAMP = 'input, textarea, select, [contenteditable=""], [contenteditable="true"]';

interface Etat {
  /** Début de la phase d'ouverture courante (horloge `maintenant`). */
  debut: number;
  /** Un geste (toucher, clic, touche) a clos la phase d'ouverture courante. */
  geste: boolean;
  /** Une saisie a eu lieu dans cette page : plus jamais d'activation imposée. */
  saisie: boolean;
  /** Moment où la page est passée en arrière-plan, ou null. */
  cacheeDepuis: number | null;
}

let horloge: () => number = () => Date.now();
const etat: Etat = { debut: horloge(), geste: false, saisie: false, cacheeDepuis: null };
const surReouverture = new Set<() => void>();

/**
 * Démarre l'observation (gestes, saisies, premier plan). À appeler une fois,
 * au plus tôt (main.tsx, via registerServiceWorker). Retourne l'arrêt (tests).
 */
export function demarrerPhaseOuverture(maintenant: () => number = () => Date.now()): () => void {
  horloge = maintenant;
  etat.debut = horloge();
  etat.geste = false;
  etat.saisie = false;
  etat.cacheeDepuis = null;

  const surGeste = (): void => {
    etat.geste = true;
  };
  const surSaisie = (e: Event): void => {
    // Une touche hors d'un champ (raccourci, Tab) est un geste, pas une saisie.
    const cible = e.target;
    if (e.type === 'keydown' && !(cible instanceof Element && cible.matches(SELECTEUR_CHAMP))) {
      etat.geste = true;
      return;
    }
    etat.geste = true;
    etat.saisie = true;
  };
  const surVisibilite = (): void => {
    if (document.visibilityState === 'hidden') {
      etat.cacheeDepuis = horloge();
      return;
    }
    const absence = etat.cacheeDepuis === null ? 0 : horloge() - etat.cacheeDepuis;
    etat.cacheeDepuis = null;
    if (absence >= ABSENCE_REOUVERTURE_MS && !etat.saisie) {
      etat.debut = horloge();
      etat.geste = false;
      for (const rappel of surReouverture) rappel();
    }
  };

  const options: AddEventListenerOptions = { capture: true, passive: true };
  document.addEventListener('pointerdown', surGeste, options);
  document.addEventListener('touchstart', surGeste, options);
  document.addEventListener('keydown', surSaisie, options);
  document.addEventListener('input', surSaisie, options);
  document.addEventListener('change', surSaisie, options);
  document.addEventListener('visibilitychange', surVisibilite);
  return () => {
    document.removeEventListener('pointerdown', surGeste, options);
    document.removeEventListener('touchstart', surGeste, options);
    document.removeEventListener('keydown', surSaisie, options);
    document.removeEventListener('input', surSaisie, options);
    document.removeEventListener('change', surSaisie, options);
    document.removeEventListener('visibilitychange', surVisibilite);
    surReouverture.clear();
  };
}

/** Abonnement aux nouvelles ouvertures (retour au premier plan après absence). */
export function quandReouverture(rappel: () => void): () => void {
  surReouverture.add(rappel);
  return () => {
    surReouverture.delete(rappel);
  };
}

/** Sommes-nous dans les premiers instants, sans geste ni saisie ? */
export function enPhaseOuverture(): boolean {
  if (etat.geste || etat.saisie) return false;
  if (document.visibilityState === 'hidden') return false;
  return horloge() - etat.debut <= DUREE_OUVERTURE_MS;
}

/** Un champ a-t-il le focus avec quelque chose dedans ? (formulaire en cours) */
export function champRempliActif(doc: Document = document): boolean {
  const actif = doc.activeElement;
  if (!(actif instanceof HTMLElement) || !actif.matches(SELECTEUR_CHAMP)) return false;
  if (actif instanceof HTMLInputElement || actif instanceof HTMLTextAreaElement) {
    if (actif instanceof HTMLInputElement && ['button', 'submit', 'reset', 'checkbox', 'radio'].includes(actif.type)) {
      return false;
    }
    return actif.value !== '';
  }
  if (actif instanceof HTMLSelectElement) return false;
  return (actif.textContent ?? '') !== '';
}

/** La garde anti-boucle autorise-t-elle un rechargement automatique ? */
export function gardeLibre(): boolean {
  try {
    const brut = window.sessionStorage.getItem(CLE_GARDE);
    if (brut === null) return true;
    const date = Number(brut);
    return !Number.isFinite(date) || horloge() - date >= DELAI_ANTI_BOUCLE_MS;
  } catch {
    // Stockage refusé : sans garde, on ne prend AUCUN risque de boucle.
    return false;
  }
}

/** Pose la garde juste avant le rechargement automatique. */
export function poserGarde(): void {
  try {
    window.sessionStorage.setItem(CLE_GARDE, String(horloge()));
  } catch {
    // gardeLibre() renverra false si le stockage est indisponible.
  }
}

/**
 * La file hors ligne (scans, mutations) est-elle vide et au repos ?
 * Prudence : au moindre doute (IndexedDB indisponible), on répond non.
 */
export async function fileHorsLigneVide(): Promise<boolean> {
  try {
    const [{ countPending }, { getSyncSnapshot }] = await Promise.all([
      import('../offline/queue'),
      import('../offline/syncManager'),
    ]);
    if (getSyncSnapshot().draining) return false;
    return (await countPending()) === 0;
  } catch {
    return false;
  }
}

export interface OptionsDecision {
  /** Active la nouvelle version (skipWaiting puis rechargement unique). */
  activer: () => void;
  /** Affiche le bandeau non bloquant. */
  signaler: () => void;
  /** Vérification de la file hors ligne (injectable pour les tests). */
  fileVide?: () => Promise<boolean>;
}

/**
 * Le cœur de la règle : active si c'est sans risque, sinon signale.
 * Retourne ce qui a été fait.
 */
export async function activerOuSignaler(options: OptionsDecision): Promise<'active' | 'signale'> {
  const fileVide = options.fileVide ?? fileHorsLigneVide;
  // Contrôles synchrones d'abord, puis la file (asynchrone), puis on
  // revérifie : un geste a pu arriver pendant la lecture d'IndexedDB.
  if (enPhaseOuverture() && !champRempliActif() && gardeLibre() && (await fileVide()) && enPhaseOuverture()) {
    poserGarde();
    options.activer();
    return 'active';
  }
  options.signaler();
  return 'signale';
}
